import React from 'react';
import { FieldLabel, MUTED, SUBTLE } from '../surface';

/**
 * Small shared bits for the two builders (email + SMS) and the audience surfaces. Everything
 * else is the surface kit; these are the three inputs the kit doesn't carry.
 */
export const inputClass = 'w-full border-2 border-black bg-white font-mono text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50 disabled:cursor-not-allowed';
export const inputStyle = { padding: '10px 12px', fontSize: 13 };

/** Email Studio's soft input (handoff §3): 46h, radius 14, grey fill, black 2px border on focus. */
export const softInputClass = 'w-full rounded-[14px] border-2 border-transparent bg-[#F5F5F5] font-mono text-[13px] text-black placeholder-[#9CA3AF] outline-none transition-colors duration-200 focus:border-black focus:bg-white';

/**
 * A soft field: 10px/700 label, the pink required star, an optional right-hand hint or counter.
 * `onWhite={false}` is for a field inside a grey block, where the input sits on white.
 */
export function SoftField({ label, required, right, rightColor = '#9CA3AF', children, htmlFor, className = '' }) {
  return (
    <label htmlFor={htmlFor} className={`flex min-w-0 flex-col ${className}`} style={{ gap: 8 }}>
      {label && (
        <span className="flex justify-between font-mono font-bold" style={{ gap: 10, fontSize: 10, letterSpacing: '.16em', color: '#6B7280' }}>
          <span>{label}{required && <span style={{ color: '#FD199F' }}> *</span>}</span>
          {right && <span style={{ color: rightColor }}>{right}</span>}
        </span>
      )}
      {children}
    </label>
  );
}

/** The soft Text: input / textarea, or the value as text when read-only. `inBlock` = 44h on white. */
export function SoftText({ value, readOnly, onChange, placeholder, rows, maxLength, id, ariaLabel, inBlock = false, caps = false, type = 'text' }) {
  const bg = inBlock ? 'bg-white' : 'bg-[#F5F5F5]';
  const style = { height: rows ? undefined : (inBlock ? 44 : 46), padding: rows ? '12px 16px' : '0 16px', lineHeight: rows ? 1.55 : undefined, textTransform: caps ? 'uppercase' : undefined, fontSize: inBlock ? 12 : 13 };
  if (readOnly) return <div className={`min-h-[44px] rounded-[14px] ${bg} px-4 py-[12px] font-mono text-[13px] whitespace-pre-wrap`} style={{ textTransform: style.textTransform }}>{value || '—'}</div>;
  const cls = softInputClass.replace('bg-[#F5F5F5]', bg);
  if (rows) return <textarea id={id} rows={rows} maxLength={maxLength} className={`${cls} resize-y`} style={style} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={ariaLabel} />;
  return <input id={id} type={type} maxLength={maxLength} className={cls} style={style} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={ariaLabel} />;
}

export function Field({ label, required, counter, hint, children, htmlFor }) {
  return (
    <div className="flex flex-col" style={{ gap: 6 }}>
      {label && (
        <div className="flex items-baseline justify-between gap-3">
          <FieldLabel required={required} htmlFor={htmlFor}>{label}</FieldLabel>
          {counter && <span className="font-mono" style={{ fontSize: 10, color: SUBTLE }}>{counter}</span>}
        </div>
      )}
      {children}
      {hint && <span className="font-mono" style={{ fontSize: 11, color: MUTED, lineHeight: 1.55 }}>{hint}</span>}
    </div>
  );
}

/** The input when editable, the value as text when not. */
export function Text({ value, readOnly, onChange, placeholder, rows, maxLength, id, ariaLabel }) {
  if (readOnly) return <div className="min-h-[40px] border-2 border-black bg-[#F5F5F5] px-3 py-[9px] font-mono text-[13px] whitespace-pre-wrap">{value || '—'}</div>;
  if (rows) return <textarea id={id} rows={rows} maxLength={maxLength} className={`${inputClass} resize-none`} style={inputStyle} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={ariaLabel} />;
  return <input id={id} maxLength={maxLength} className={inputClass} style={inputStyle} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={ariaLabel} />;
}

/**
 * An ISO instant as the wall-clock string <input type="datetime-local"> expects, in the
 * viewer's own timezone. The input has no timezone of its own, so the conversion happens on
 * both edges.
 */
export function toLocalInputValue(iso) {
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const countWords = (s) => (s || '').split(/\s+/).filter(Boolean).length;

export const plural = (n, one, many = `${one}s`) => `${Number(n || 0).toLocaleString()} ${n === 1 ? one : many}`;
